import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useListUsers, useUpdateUserRoles } from '@/api/admin/admin';
import { Roles } from '@/api/model/roles';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Search, Settings, RefreshCw } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api-error';

export const UserManagementPage: React.FC = () => {
  const { currentUserId } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('alle');
  const [selectedStatus, setSelectedStatus] = useState<string>('alle');
  const [roleDialog, setRoleDialog] = useState<{
    open: boolean;
    userId?: string;
    currentRoles?: Roles[];
  }>({ open: false });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [roleForm, setRoleForm] = useState<{
    roles: Roles[];
  }>({ roles: [] });

  // API hooks
  const {
    data: usersData,
    refetch,
    isLoading,
    isError,
    error,
  } = useListUsers({
    limit: 100,
    offset: 0,
  });
  const updateRolesMutation = useUpdateUserRoles();

  // Client-side filtering
  const users = React.useMemo(() => {
    const allUsers = usersData?.users ?? [];
    let filtered = [...allUsers];

    // Apply search filter
    if (debouncedSearch.trim()) {
      const searchTerm = debouncedSearch.trim().toLowerCase();
      filtered = filtered.filter(user => {
        const email = (user.email || '').toLowerCase();
        const username = (user.username || '').toLowerCase();
        return email.includes(searchTerm) || username.includes(searchTerm);
      });
    }

    // Apply role filter
    if (selectedRole !== 'alle') {
      if (selectedRole === 'admin') {
        filtered = filtered.filter(user => user.roles.includes(Roles.Admin));
      } else if (selectedRole === 'user') {
        filtered = filtered.filter(user => !user.roles.includes(Roles.Admin));
      }
    }

    // Apply status filter
    if (selectedStatus !== 'alle') {
      filtered = filtered.filter(user => {
        if (selectedStatus === 'aktiv') {
          return user.enabled;
        } else if (selectedStatus === 'deaktiviert') {
          return !user.enabled;
        }
        return true;
      });
    }

    return filtered;
  }, [usersData?.users, debouncedSearch, selectedRole, selectedStatus]);

  const handleRoleUpdate = async () => {
    if (!roleDialog.userId || roleForm.roles.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Rolle aus (Benutzer oder Administrator).');
      return;
    }
    if (currentUserId != null && roleDialog.userId === currentUserId) {
      toast.error('Eigenen Admin-Status nicht bearbeitbar');
      return;
    }

    try {
      await updateRolesMutation.mutateAsync({
        userId: roleDialog.userId,
        data: { roles: roleForm.roles },
      });
      toast.success('Admin-Status aktualisiert');
      setRoleDialog({ open: false });
      setRoleForm({ roles: [] });
      refetch();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Fehler beim Aktualisieren des Admin-Status'));
    }
  };

  // Initialize role form when dialog opens
  React.useEffect(() => {
    if (roleDialog.open && roleDialog.currentRoles) {
      setRoleForm({ roles: roleDialog.currentRoles });
    }
  }, [roleDialog.open, roleDialog.currentRoles]);

  return (
    <div className='space-y-6'>
      {/* Page header */}
      <section className='flex items-end justify-between gap-4'>
        <div className='flex flex-col gap-2'>
          <CardTitle className='text-swd-tuerkis-darker text-3xl leading-9 font-semibold'>Nutzerverwaltung</CardTitle>
          <CardDescription className='text-neutral-grau tracking-wide'>
            Admin-Status von Benutzern verwalten
          </CardDescription>
        </div>
      </section>

      {/* Toolbar - search and filters */}
      <section className='flex items-center justify-between gap-3 flex-wrap rounded-xl border border-neutral-hellgrau bg-white px-3 py-4'>
        <div className='flex items-center gap-3 flex-wrap flex-1'>
          {/* Search */}
          <div className='relative flex-1 min-w-[200px]'>
            <Input
              placeholder='Nach E-Mail, Benutzername oder ID suchen...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='pr-8'
            />
            <Search className='absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none' />
          </div>

          {/* Role Filter */}
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger size='sm'>
              <SelectValue placeholder='Alle Rollen' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='alle'>Alle Rollen</SelectItem>
              <SelectItem value='admin'>Administrator</SelectItem>
              <SelectItem value='user'>Benutzer</SelectItem>
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger size='sm'>
              <SelectValue placeholder='Status Alle' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='alle'>Status Alle</SelectItem>
              <SelectItem value='aktiv'>Aktiv</SelectItem>
              <SelectItem value='deaktiviert'>Deaktiviert</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Content area */}
      {isLoading ? (
        <div className='text-center py-8 bg-white rounded-xl border border-neutral-hellgrau'>
          <RefreshCw className='h-8 w-8 text-neutral-hellgrau mx-auto mb-4 animate-spin' />
          <p className='text-muted-foreground mb-4'>Benutzer werden geladen...</p>
        </div>
      ) : isError ? (
        <div className='text-center py-8 bg-white rounded-xl border border-neutral-hellgrau'>
          <Users className='h-8 w-8 text-red-500 mx-auto mb-4' />
          <p className='text-red-500 mb-4'>
            Fehler beim Laden der Benutzer: {error instanceof Error ? error.message : 'Unbekannter Fehler'}
          </p>
        </div>
      ) : users.length === 0 ? (
        <div className='text-center py-8 bg-white rounded-xl border border-neutral-hellgrau'>
          <Users className='h-8 w-8 text-neutral-hellgrau mx-auto mb-4' />
          <p className='text-muted-foreground mb-4'>
            {searchQuery ? 'Keine Benutzer gefunden' : 'Noch keine Benutzer vorhanden.'}
          </p>
        </div>
      ) : (
        <div className='rounded-xl border border-neutral-hellgrau overflow-hidden bg-white'>
          <Table className='min-w-[960px]'>
            <TableHeader className='bg-swd-hellblau'>
              <TableRow>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  E-Mail
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Rollen
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Status
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap w-[150px] text-center'>
                  Aktionen
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(user => {
                const isCurrentUser = currentUserId != null && user.id === currentUserId;
                return (
                  <TableRow
                    key={user.id}
                    className={`border-b border-neutral-hellgrau ${
                      isCurrentUser ? 'opacity-60 pointer-events-none' : 'hover:bg-swd-hellblau/40'
                    }`}
                  >
                    <TableCell className='font-medium text-neutral-dunkelgrau align-middle'>
                      <div className='flex items-center gap-2'>
                        <span>{user.email || '—'}</span>
                        {isCurrentUser && (
                          <span className='text-xs font-medium text-muted-foreground whitespace-nowrap'>
                            (Ihr Konto)
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className='align-middle'>
                      <div className='flex gap-1 flex-wrap'>
                        {user.roles.map(role => (
                          <span
                            key={role}
                            className='px-2 py-1 text-xs rounded-md bg-swd-hellblau text-swd-tuerkis-darker font-medium'
                          >
                            {role === Roles.Admin ? 'Administrator' : 'Benutzer'}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className='align-middle'>
                      <span
                        className={`px-2 py-1 text-xs rounded-md font-medium ${
                          user.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.enabled ? 'Aktiv' : 'Deaktiviert'}
                      </span>
                    </TableCell>
                    <TableCell className='text-center align-middle'>
                      <div className='flex items-center justify-center gap-2'>
                        <Button
                          variant='ghost'
                          size='sm'
                          className='h-8 w-8 p-0'
                          onClick={() =>
                            setRoleDialog({
                              open: true,
                              userId: user.id,
                              currentRoles: user.roles,
                            })
                          }
                          title='Admin-Status verwalten'
                        >
                          <Settings className='h-4 w-4 text-swd-tuerkis-darker' />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Role Update Dialog */}
      <AlertDialog open={roleDialog.open} onOpenChange={open => setRoleDialog({ open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Admin-Status verwalten</AlertDialogTitle>
            <AlertDialogDescription>
              Benutzer als Administrator freigeben oder die Berechtigung entziehen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='py-4 space-y-4'>
            <div>
              <Label>Admin-Status</Label>
              <div className='mt-2 space-y-2'>
                {[Roles.User, Roles.Admin].map(role => (
                  <div key={role} className='flex items-center space-x-2'>
                    <Checkbox
                      id={`role-${role}`}
                      checked={roleForm.roles.includes(role)}
                      onCheckedChange={checked => {
                        if (checked) {
                          setRoleForm(prev => ({
                            roles: [...prev.roles, role],
                          }));
                        } else {
                          setRoleForm(prev => ({
                            roles: prev.roles.filter(r => r !== role),
                          }));
                        }
                      }}
                    />
                    <Label htmlFor={`role-${role}`} className='font-normal cursor-pointer'>
                      {role === Roles.Admin ? 'Administrator' : 'Benutzer'}
                    </Label>
                  </div>
                ))}
              </div>
              <p className='text-xs text-muted-foreground mt-2'>Mindestens eine Rolle muss ausgewählt werden.</p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setRoleDialog({ open: false });
                setRoleForm({ roles: [] });
              }}
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRoleUpdate} disabled={updateRolesMutation.isPending} variant='enabled'>
              {updateRolesMutation.isPending && <RefreshCw className='h-4 w-4 animate-spin mr-2' />}
              Admin-Status aktualisieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
